import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { Briefcase } from "lucide-react"
import { jobsApi } from "@/api/jobs"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { PageHeader } from "@/components/dashboard/page-header"
import { EmptyState } from "@/components/shared/empty-state"
import { DetailPageSkeleton } from "@/components/shared/loading-skeletons"
import { formatDate } from "@/lib/utils"

export function JobDetailPage() {
  const { id = "" } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const job = useQuery({ queryKey: ["job", id], queryFn: () => jobsApi.get(id), enabled: !!id })

  const publish = useMutation({
    mutationFn: () => jobsApi.publish(id),
    onSuccess: () => {
      toast.success("İlan yayımlandı.")
      qc.invalidateQueries({ queryKey: ["job", id] })
    },
  })
  const close = useMutation({
    mutationFn: () => jobsApi.close(id),
    onSuccess: () => {
      toast.success("İlan kapatıldı.")
      qc.invalidateQueries({ queryKey: ["job", id] })
    },
  })
  const remove = useMutation({
    mutationFn: () => jobsApi.remove(id),
    onSuccess: () => {
      toast.success("İlan silindi.")
      nav("/dashboard/jobs")
    },
  })

  if (job.isLoading) return <DetailPageSkeleton />
  if (!job.data)
    return (
      <div className="p-6">
        <EmptyState
          icon={Briefcase}
          title="İlan bulunamadı"
          description="Aradığın ilan silinmiş ya da erişim yetkiniz yok."
          action={
            <Link to="/dashboard/jobs">
              <Button variant="outline">İlanlara dön</Button>
            </Link>
          }
        />
      </div>
    )
  const j = job.data.data

  return (
    <>
      <PageHeader
        title={j.title}
        description={`${j.location ?? "—"} · ${j.employment_type} · ${formatDate(j.created_at)}`}
        action={
          <div className="flex gap-2">
            <Link to={`/dashboard/jobs/${id}/edit`}>
              <Button variant="outline">Düzenle</Button>
            </Link>
            {j.status !== "open" && (
              <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
                Yayımla
              </Button>
            )}
            {j.status === "open" && (
              <Button variant="outline" onClick={() => close.mutate()} disabled={close.isPending}>
                Kapat
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => {
                if (confirm("Bu ilanı silmek istediğinden emin misin?")) remove.mutate()
              }}
            >
              Sil
            </Button>
          </div>
        }
      />
      <div className="space-y-4 p-4 sm:p-6">
        <Card>
          <CardContent className="space-y-4 p-6">
            <section>
              <h2 className="mb-1 text-sm font-medium text-muted-foreground">İş tanımı</h2>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{j.description || "—"}</p>
            </section>
            <section>
              <h2 className="mb-1 text-sm font-medium text-muted-foreground">Gereksinimler</h2>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{j.requirements || "—"}</p>
            </section>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-3 p-6 text-sm md:grid-cols-2">
            <Field label="Slug" value={j.slug} />
            <Field label="Departman" value={j.department || "—"} />
            <Field label="Lokasyon" value={j.location || "—"} />
            <Field label="Çalışma şekli" value={j.employment_type} />
            <Field
              label="Maaş aralığı"
              value={
                j.salary_min || j.salary_max
                  ? `${j.salary_min ?? "—"} – ${j.salary_max ?? "—"} ${j.currency}`
                  : "—"
              }
            />
            <Field label="Yayımlanma" value={formatDate(j.published_at)} />
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  )
}
